import { useEffect, useRef, useState } from 'preact/hooks'
import { navigate } from 'raviger'

import {
  completeWorkout,
  home,
  loadHome,
  sendFeedback,
  startWorkout
} from '../utils/home'
import { ratingOf, ratings } from '../utils/rating'
import { formatDuration } from '../utils/workout'
import { WorkoutMeta } from './workout_card'

const REWIND_S = 5

const completeErrors = {
  too_early: 'El servidor dice que aún no ha pasado el tiempo del workout.',
  not_started: 'El workout no constaba como empezado. Vuelve a intentarlo.',
  outside_challenge: 'Hoy no hay reto.'
}

export function Play() {
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  useEffect(() => {
    if (home.value === undefined)
      loadHome().catch((e) => setError(e.code))
  }, [])

  const data = home.value
  const playable = Boolean(data?.today?.workout)

  useEffect(() => {
    if (data && !playable)
      navigate('/', { replace: true })
  }, [data, playable])
  if (error)
    return (
      <main class="page play">
        <p class="error">{error}</p>
        <a href="/">Volver</a>
      </main>
    )
  if (!data)
    return null

  const { today, calendar } = data
  if (!playable)
    return null

  if (result)
    return <Done result={result} play={today.play} />
  if (calendar.doneToday)
    return (
      <AlreadyDone streak={data.profile.currentStreak} play={today.play} />
    )

  return (
    <main class="page play">
      <p class="workout-day muted">Día {today.day}</p>
      <h1>{today.workout.title}</h1>
      <WorkoutMeta workout={today.workout} />
      <Player
        day={today.day}
        started={Boolean(today.play)}
        onComplete={setResult}
      />
      <a class="muted back" href="/">Volver sin terminar</a>
    </main>
  )
}

// The audio element plus the rules of the challenge: no seeking past
// what has been heard, no speeding up. maxReached is the furthest
// point played so far; a forward seek snaps back to it.
function Player({ day, started, onComplete }) {
  const audio = useRef(null)
  const maxReached = useRef(0)
  const startedRef = useRef(started)
  const [playing, setPlaying] = useState(false)
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)
  const [completing, setCompleting] = useState(false)

  useEffect(() => {
    const el = audio.current
    const onTime = () => {
      if (!el.seeking && el.currentTime > maxReached.current)
        maxReached.current = el.currentTime
      setTime(el.currentTime)
    }
    const onSeeking = () => {
      if (el.currentTime > maxReached.current + 0.5)
        el.currentTime = maxReached.current
    }
    const onRate = () => {
      if (el.playbackRate !== 1)
        el.playbackRate = 1
    }
    const onMeta = () => setDuration(el.duration)
    const onPlay = () => setPlaying(true)
    const onPause = () => setPlaying(false)
    const onError = () => setError('No se ha podido cargar el audio.')

    el.addEventListener('timeupdate', onTime)
    el.addEventListener('seeking', onSeeking)
    el.addEventListener('ratechange', onRate)
    el.addEventListener('loadedmetadata', onMeta)
    el.addEventListener('durationchange', onMeta)
    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPause)
    el.addEventListener('ended', finish)
    el.addEventListener('error', onError)

    return () => {
      el.pause()
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('seeking', onSeeking)
      el.removeEventListener('ratechange', onRate)
      el.removeEventListener('loadedmetadata', onMeta)
      el.removeEventListener('durationchange', onMeta)
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('ended', finish)
      el.removeEventListener('error', onError)
    }
  }, [])

  // play() goes first and synchronously, inside the click, so mobile
  // autoplay rules let it through; the start call runs alongside.
  function toggle() {
    const el = audio.current
    if (!el.paused) {
      el.pause()
      return
    }

    setError(null)
    el.play().catch(() => setError('No se ha podido reproducir.'))
    if (!startedRef.current) {
      startedRef.current = true
      startWorkout().catch((e) => {
        startedRef.current = false
        el.pause()
        setError(`No se ha podido empezar el workout (${e.code}).`)
      })
    }
  }

  function rewind() {
    const el = audio.current
    el.currentTime = Math.max(0, el.currentTime - REWIND_S)
  }

  async function finish() {
    setCompleting(true)
    setStatus('Guardando…')
    try {
      onComplete(await completeWorkout())
    } catch (e) {
      if (e.code === 'already_done') {
        await loadHome()
        return
      }
      const detail = e.code === 'too_early' && e.body?.remainingS
        ? ` Faltan ${e.body.remainingS} s.`
        : ''
      setError((completeErrors[e.code] || e.code) + detail)
      setStatus(null)
    } finally {
      setCompleting(false)
    }
  }

  const progress = duration ? (time / duration) * 100 : 0

  return (
    <section class="player">
      <audio ref={audio} src={`/audio/${day}`} preload="auto" playsinline />
      <div class="progress" role="progressbar" aria-valuenow={progress}>
        <div class="progress-bar" style={{ width: `${progress}%` }} />
      </div>
      <p class="times muted">
        <span>{formatDuration(time)}</span>
        <span>{duration ? formatDuration(duration) : '–:––'}</span>
      </p>
      <div class="player-controls">
        <button
          class="round"
          onClick={rewind}
          disabled={completing}
          aria-label={`Retroceder ${REWIND_S} segundos`}
        >
          −{REWIND_S} s
        </button>
        <button
          class="primary round main"
          onClick={toggle}
          disabled={completing}
          aria-label={playing ? 'Pausar' : 'Reproducir'}
        >
          {playing ? '❚❚' : '▶'}
        </button>
      </div>
      {status && <p class="muted">{status}</p>}
      {error && (
        <p class="error">
          {error}{' '}
          {error.startsWith('El servidor') && (
            <button class="link" onClick={finish}>Reintentar</button>
          )}
        </p>
      )}
    </section>
  )
}

function Done({ result, play }) {
  const days = result.streak === 1 ? 'día' : 'días'

  return (
    <main class="page play done">
      <h1>¡Workout hecho!</h1>
      <p class="streak">
        Tu racha sube a <strong>{result.streak}</strong> {days}.
        {result.newRecord && result.streak > 1 && ' ¡Nuevo récord!'}
      </p>
      <p class="muted">
        Llevas {result.points} {result.points === 1 ? 'punto' : 'puntos'}.
      </p>
      <Feedback play={play} />
      <a class="button primary" href="/">Volver al calendario</a>
    </main>
  )
}

function AlreadyDone({ streak, play }) {
  return (
    <main class="page play done">
      <h1>Hoy ya está hecho</h1>
      <p class="streak">
        Racha actual: <strong>{streak}</strong> {streak === 1 ? 'día' : 'días'}.
      </p>
      <Feedback play={play} />
      <a class="button primary" href="/">Volver al calendario</a>
    </main>
  )
}

const feedbackErrors = {
  bad_rating: 'Elige una carita.',
  bad_comment: 'La opinión es demasiado larga.',
  not_done: 'El workout no consta como terminado.',
  not_started: 'El workout no consta como empezado.',
  outside_challenge: 'Hoy no hay reto.'
}

// Rating and free-text opinion of the play just completed. The form
// hides itself once home says the play has a rating.
function Feedback({ play }) {
  const [rating, setRating] = useState(play?.rating ?? null)
  const [comment, setComment] = useState(play?.comment ?? '')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  if (!play)
    return null
  if (play.rating) {
    const chosen = ratingOf(play.rating)

    return (
      <section class="feedback">
        <p class="muted">
          Gracias por tu opinión {chosen?.face}
        </p>
      </section>
    )
  }

  async function submit(event) {
    event.preventDefault()
    if (!rating) {
      setError(feedbackErrors.bad_rating)
      return
    }
    setError(null)
    setBusy(true)
    try {
      await sendFeedback(rating, comment)
    } catch (e) {
      setError(feedbackErrors[e.code] || e.code)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="feedback" onSubmit={submit}>
      <p class="feedback-title">¿Qué te ha parecido?</p>
      <div class="rating" role="radiogroup" aria-label="Nota del workout">
        {ratings.map((r) => (
          <button
            key={r.value}
            type="button"
            class={`rating-option${rating === r.value ? ' selected' : ''}`}
            role="radio"
            aria-checked={rating === r.value}
            aria-label={`${r.value}: ${r.label}`}
            title={r.label}
            onClick={() => setRating(r.value)}
            disabled={busy}
          >
            <span class="face">{r.face}</span>
            <span class="number">{r.value}</span>
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        maxLength={1000}
        rows={3}
        placeholder="Cuéntanos qué tal (opcional)"
        onInput={(e) => setComment(e.currentTarget.value)}
        disabled={busy}
      />
      {error && <p class="error">{error}</p>}
      <button class="primary" type="submit" disabled={busy}>
        Enviar opinión
      </button>
    </form>
  )
}
